import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalLayout, type LegalSection } from "@/components/legal-layout";
import { identificacaoEmpresa, LEGAL } from "@/lib/legal";

/** Termos de Uso públicos (URL cadastrada no app Meta). */
export const Route = createFileRoute("/termos")({
  component: TermosPage,
});

const sections: LegalSection[] = [
  {
    title: "Aceite",
    body: (
      <p>
        Estes termos regem o uso do {LEGAL.produto}, oferecido por {identificacaoEmpresa()}. Ao
        criar uma conta você declara que leu e concorda com eles e com a{" "}
        <Link to="/privacidade" className="underline">
          Política de Privacidade
        </Link>
        . Se você usa o sistema em nome de uma empresa, declara ter poderes para aceitá-los por ela.
      </p>
    ),
  },
  {
    title: "O serviço",
    body: (
      <p>
        O {LEGAL.produto} permite conectar contas da Meta (Facebook e Instagram) e gerenciar
        Business Managers, contas de anúncio, campanhas, criativos, públicos, relatórios, regras
        automáticas, alertas e leads de vários clientes num só lugar. O serviço depende das APIs da
        Meta e pode ser afetado por mudanças, limites ou indisponibilidades delas.
      </p>
    ),
  },
  {
    title: "Sua conta e sua equipe",
    body: (
      <ul>
        <li>Você é responsável pelas credenciais da sua conta e pelos membros que convidar.</li>
        <li>
          Os papéis (Dono, Admin, Gestor, Analista, Cliente final) definem o que cada pessoa pode
          ver e alterar; as ações ficam registradas em log de auditoria.
        </li>
        <li>Você só deve conectar contas Meta que tenha autorização para gerenciar.</li>
      </ul>
    ),
  },
  {
    title: "Seus anúncios e a Meta",
    body: (
      <ul>
        <li>
          Você é o único responsável pelo conteúdo, pela segmentação, pelos orçamentos e pelos
          resultados dos anúncios, e por cumprir as Políticas de Publicidade e os Termos da Meta.
        </li>
        <li>
          Os valores gastos em anúncios são cobrados pela Meta diretamente na sua conta de anúncio,
          não pelo {LEGAL.produto}.
        </li>
        <li>
          Regras automáticas e sugestões do assistente de IA só agem dentro do que você configurar;
          revise-as antes de ativar. A IA sugere, quem decide é você.
        </li>
      </ul>
    ),
  },
  {
    title: "Uso proibido",
    body: (
      <ul>
        <li>
          Contornar bloqueios, restrições ou banimentos da Meta (por exemplo, contas de
          "contingência" ou troca de conta para fugir de restrição).
        </li>
        <li>Anunciar produtos ou conteúdos ilegais ou proibidos pelas políticas da Meta.</li>
        <li>Usar dados de leads ou públicos sem base legal ou sem o consentimento exigido.</li>
        <li>Tentar acessar dados de outras agências, sobrecarregar ou atacar o sistema.</li>
      </ul>
    ),
  },
  {
    title: "Planos, teste grátis e pagamento",
    body: (
      <ul>
        <li>Novas contas têm 7 dias de teste grátis, sem cartão.</li>
        <li>
          Depois, o uso depende de uma assinatura mensal ou anual, cobrada antecipadamente pelo
          Stripe (cartão ou boleto). Os limites de cada plano aparecem na página de planos.
        </li>
        <li>
          Se o pagamento atrasar mais de 3 dias ou o teste acabar sem assinatura, a conta entra em
          modo leitura: você continua vendo e exportando seus dados, mas não pode criar nem alterar
          nada até regularizar.
        </li>
        <li>
          Você pode cancelar quando quiser; o acesso continua até o fim do período já pago. Não há
          reembolso proporcional, salvo exigência legal.
        </li>
      </ul>
    ),
  },
  {
    title: "Dados e privacidade",
    body: (
      <p>
        Em relação aos dados dos seus clientes e leads, sua agência é a controladora e o{" "}
        {LEGAL.produto} atua como operador, nos termos da LGPD e da{" "}
        <Link to="/privacidade" className="underline">
          Política de Privacidade
        </Link>
        . Você pode exportar seus dados a qualquer momento e pedir a exclusão completa da conta.
      </p>
    ),
  },
  {
    title: "Disponibilidade e responsabilidade",
    body: (
      <ul>
        <li>
          Trabalhamos para manter o serviço disponível, mas ele é fornecido "como está", podendo ter
          interrupções para manutenção ou por falhas de terceiros (incluindo a Meta).
        </li>
        <li>
          Não respondemos por perdas indiretas, lucros cessantes, resultados de campanhas,
          reprovações ou bloqueios aplicados pela Meta.
        </li>
        <li>
          Nossa responsabilidade total fica limitada ao valor pago pelo serviço nos 12 meses
          anteriores ao fato.
        </li>
      </ul>
    ),
  },
  {
    title: "Suspensão e encerramento",
    body: (
      <p>
        Podemos suspender contas que violem estes termos ou as políticas da Meta, ou que estejam
        inadimplentes. Ao encerrar a conta, os dados são apagados conforme a Política de
        Privacidade.
      </p>
    ),
  },
  {
    title: "Alterações, contato e foro",
    body: (
      <>
        <p>
          Podemos atualizar estes termos; mudanças relevantes serão avisadas com antecedência por
          e-mail ou dentro do sistema. Suporte:{" "}
          <a className="underline" href={`mailto:${LEGAL.emailSuporte}`}>
            {LEGAL.emailSuporte}
          </a>
          .
        </p>
        <p>
          Estes termos seguem as leis do Brasil. Fica eleito o foro da comarca de{" "}
          {LEGAL.cidadeForo ?? "domicílio da sede da IA GENIX"} para resolver eventuais disputas.
        </p>
      </>
    ),
  },
];

function TermosPage() {
  return <LegalLayout title="Termos de Uso" sections={sections} />;
}
